import { ref, computed, readonly } from 'vue'
import { useCookies } from '@vueuse/integrations/useCookies'
import { useStorage } from '@vueuse/core'
import { useToasts } from './toasts'
import { INFO } from '@/constants/icons'
import { useApp } from './app'
import { BROKER, CLIENT, companyRoles, VENDOR } from '@/constants/permissions'
import { findBestMatchingRoute } from '@/utils/routes/finder'

// Application version constants
const APPLICATION_VERSIONS = {
  RAILS: 1,
  VUE2: 2,
  VUE3: 3
} as const

export interface VersionSwitchResult {
  success: boolean
  error?: Error
  message?: string
  fromVersion?: 1 | 2 | 3
  toVersion?: 1 | 2 | 3
  newRoute?: string | null
  requiresReload?: boolean
}
export interface VersionMapping {
  label: string
  iconLabel: string
  version: 'classic' | 'legacy' | 'advanced'
  rawValue: 1 | 2 | 3
}

export const APP_VERSION_MAPPING: VersionMapping[] = [
  { label: 'Version 1', iconLabel: 'V1', version: 'classic', rawValue: 1 },
  { label: 'Version 2', iconLabel: 'V2', version: 'legacy', rawValue: 2 },
  { label: 'Version 3', iconLabel: 'V3', version: 'advanced', rawValue: 3 }
]

export type VersionRawValues = typeof APPLICATION_VERSIONS[keyof typeof APPLICATION_VERSIONS]

/**
 * Return the VUE2 version configuration - version as of now is - 'legacy'
 */
export const VUE2_VERSION_CONFIG = APP_VERSION_MAPPING.find((info: VersionMapping) => info.version === 'legacy')

const availableVersions = ref<VersionMapping[]>([])

export const useApplicationVersion = (route: any = null, router: any = null) => {
  const applicationVersion = useStorage<number>(
    'application-version',
    APPLICATION_VERSIONS.RAILS
  )

  const { get: getCookie, set: setCookie } = useCookies(['vue_version_preference'])

  const vueVersion = ref<2 | 3>(getCookie('vue_version_preference') === 'vue3' ? 3 : 2)
  const isTransitioning = ref(false)

  const { addToast } = useToasts()
  const { companyWizardRef, companyType } = useApp()

  // Initialize Vue version from cookie if present
  const vuePreference = ref<string>(getCookie('vue_version_preference'))
  if (vuePreference.value === 'vue3') {
    vueVersion.value = APPLICATION_VERSIONS.VUE3
  } else {
    vueVersion.value = APPLICATION_VERSIONS.VUE2
  }

  // Computed properties for version checks
  const isRailsVersion = computed(() => applicationVersion.value === APPLICATION_VERSIONS.RAILS)
  const isVue2Version = computed(() => applicationVersion.value !== APPLICATION_VERSIONS.RAILS && vueVersion.value === APPLICATION_VERSIONS.VUE2)
  const isVue3Version = computed(() => applicationVersion.value !== APPLICATION_VERSIONS.RAILS && vueVersion.value === APPLICATION_VERSIONS.VUE3)
  const isAnyVueVersion = computed(() => applicationVersion.value !== APPLICATION_VERSIONS.RAILS)

  // Set version for Rails/Vue toggle
  function setVersion (version: 1 | 2 | 3): void {
    if (version === APPLICATION_VERSIONS.RAILS) {
      applicationVersion.value = APPLICATION_VERSIONS.RAILS
    } else if (version === APPLICATION_VERSIONS.VUE2) {
      applicationVersion.value = 2 // Store as 2 for backward compatibility
      vueVersion.value = APPLICATION_VERSIONS.VUE2
      setVueVersionCookie('vue2')
    } else if (version === APPLICATION_VERSIONS.VUE3) {
      applicationVersion.value = 2 // Store as 2 for backward compatibility (any Vue)
      vueVersion.value = APPLICATION_VERSIONS.VUE3
      setVueVersionCookie('vue3')
    } else {
      console.error('[ApplicationVersion] Invalid version', version)
    }
  }

  const setVueVersionCookie = (version: 'vue2' | 'vue3'): void => {
    setCookie('vue_version_preference', version, {
      path: '/',
      maxAge: 60 * 60 * 24 * 60, // 60 days
      sameSite: 'lax'
    })
    vuePreference.value = version
  }

  async function switchVersionInitial (target: VersionRawValues): Promise<void> {
    const fromVersionInfo = APP_VERSION_MAPPING.find((info: { label: string, rawValue: VersionRawValues }) => info.rawValue === applicationVersion.value)
    const targetVersionInfo = APP_VERSION_MAPPING.find((info: { label: string, rawValue: VersionRawValues }) => info.rawValue === target)
    const fromVersion = fromVersionInfo?.version || 'legacy'
    const targetVersion = targetVersionInfo?.version || 'classic'

    const attrToSend: VersionSwitchResult = {
      success: true,
      message: targetVersionInfo ? `Switching to ${targetVersionInfo.label}` : '',
      requiresReload: target === 3,
      newRoute: null
    }

    try {
      if (isTransitioning.value) {
        attrToSend.success = false
        attrToSend.message = 'Version switch already in progress.'
      } else {
        isTransitioning.value = true
        const result = findBestMatchingRoute(
          route,
          fromVersion,
          targetVersion,
          companyType.value || 'broker'
        )
        attrToSend.newRoute = result.route

        if (result.reason) {
          attrToSend.success = false
          attrToSend.message = result.reason
        }
        // else if (!result.isAccessible) {
        //   // User doesn't have permissions to requested route - should show no-access page ?
        //   // Current: Delegate permissions handling to the target module
        //   // attrToSend.newRoute = '/no-access'
        // }
        if (result.isExceptionSuccess && attrToSend.success) {
          attrToSend.message = targetVersionInfo
            ? `Switched to ${targetVersionInfo.label}`
            : 'Version has been Switched.'
        }
      }
    } catch (error) {
      attrToSend.success = false
      attrToSend.message = '[VersionSwitcher] Error switching version'
      console.error(attrToSend.message, error)
    } finally {
      isTransitioning.value = false
      addToast({
        timeout: 5000,
        message: attrToSend.message,
        color: attrToSend.success ? 'success' : 'error'
      })
      if (attrToSend.success && attrToSend.newRoute) {
        applicationVersion.value = target
        if (attrToSend.requiresReload) {
          setVueVersionCookie('vue3')
          window.location.assign(attrToSend.newRoute)
        } else {
          router.push(attrToSend.newRoute)
        }
      }
    }
  }

  // Get current version for display purposes
  const getCurrentVersion = (): 1 | 2 | 3 => {
    if (applicationVersion.value === APPLICATION_VERSIONS.RAILS) {
      return APPLICATION_VERSIONS.RAILS
    }
    return vueVersion.value
  }

  // Get version label for display
  const getVersionLabel = (): string => {
    if (applicationVersion.value === APPLICATION_VERSIONS.RAILS) {
      return 'Classic Version'
    }
    if (vueVersion.value === APPLICATION_VERSIONS.VUE3) {
      return 'Vue 3 Version'
    }
    return 'Vue 2 Version'
  }

  function setAvailableVersions (availableActiveVersions: VersionMapping['version'][]) {
    availableVersions.value = APP_VERSION_MAPPING.filter((info: VersionMapping) => {
      return availableActiveVersions.includes(info.version)
    })
    const fallback = availableVersions.value[0]
    if (fallback && !availableVersions.value.some((info: VersionMapping) => info.rawValue === applicationVersion.value)) {
      applicationVersion.value = fallback.rawValue
    }
  }

  const selectedVersion = computed(() => {
    return availableVersions.value.find((version: VersionMapping) => {
      return version.rawValue === applicationVersion.value
    })
  })

  const isClassicAllowed = computed(() => {
    return !!availableVersions.value.find((info: VersionMapping) => info.version === 'classic')
  })

  return {
    applicationVersion,
    vueVersion,
    availableVersions: readonly(availableVersions),
    selectedVersion,
    isRailsVersion,
    isVue2Version,
    isVue3Version,
    isAnyVueVersion,
    setVersion,
    getCurrentVersion,
    getVersionLabel,
    setAvailableVersions,
    isTransitioning,
    isClassicAllowed: readonly(isClassicAllowed),
    switchVersionInitial
  }
}
